import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import OrdersList from "@/pages/OrdersList";
import OrderCreate from "@/pages/OrderCreate";
import OrderDetail from "@/pages/OrderDetail";
import Customers from "@/pages/admin/Customers";
import CustomerPricing from "@/pages/admin/CustomerPricing";
import DriverPayouts from "@/pages/admin/DriverPayouts";
import DriverMyPayouts from "@/pages/driver/DriverMyPayouts";
import Billing from "@/pages/admin/Billing";
import Templates from "@/pages/Templates";
import Users from "@/pages/admin/Users";
import Drivers from "@/pages/admin/Drivers";
import Vehicles from "@/pages/admin/Vehicles";
import Reports from "@/pages/admin/Reports";
import Settings from "@/pages/admin/Settings";
import MapView from "@/pages/admin/MapView";
import AddressBook from "@/pages/AddressBook";
import DriverTasks from "@/pages/driver/DriverTasks";
import DriverOrderDetail from "@/pages/driver/DriverOrderDetail";
import OfflineIndicator from "@/components/OfflineIndicator";
import { Toaster } from "@/components/ui/sonner";

function RootRedirect() {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "customer") return <Navigate to="/portal" replace />;
  if (user.role === "driver") return <Navigate to="/driver" replace />;
  return <Navigate to="/admin" replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <OfflineIndicator />
        <Toaster position="top-center" richColors closeButton />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />

          {/* Admin / Dispatcher */}
          <Route path="/admin" element={<ProtectedRoute roles={["admin","dispatcher"]}><Dashboard /></ProtectedRoute>} />
          <Route path="/admin/orders" element={<ProtectedRoute roles={["admin","dispatcher"]}><OrdersList /></ProtectedRoute>} />
          <Route path="/admin/orders/new" element={<ProtectedRoute roles={["admin","dispatcher"]}><OrderCreate /></ProtectedRoute>} />
          <Route path="/admin/orders/:id/edit" element={<ProtectedRoute roles={["admin","dispatcher"]}><OrderCreate editMode={true} /></ProtectedRoute>} />
          <Route path="/admin/orders/:id" element={<ProtectedRoute roles={["admin","dispatcher"]}><OrderDetail /></ProtectedRoute>} />
          <Route path="/admin/customers" element={<ProtectedRoute roles={["admin","dispatcher"]}><Customers /></ProtectedRoute>} />
          <Route path="/admin/customers/:id/pricing" element={<ProtectedRoute roles={["admin"]}><CustomerPricing /></ProtectedRoute>} />
          <Route path="/admin/drivers/:id/payouts" element={<ProtectedRoute roles={["admin"]}><DriverPayouts /></ProtectedRoute>} />
          <Route path="/admin/billing" element={<ProtectedRoute roles={["admin","dispatcher"]}><Billing /></ProtectedRoute>} />
          <Route path="/admin/templates" element={<ProtectedRoute roles={["admin","dispatcher"]}><Templates /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute roles={["admin"]}><Users /></ProtectedRoute>} />
          <Route path="/admin/drivers" element={<ProtectedRoute roles={["admin","dispatcher"]}><Drivers /></ProtectedRoute>} />
          <Route path="/admin/vehicles" element={<ProtectedRoute roles={["admin"]}><Vehicles /></ProtectedRoute>} />
          <Route path="/admin/reports" element={<ProtectedRoute roles={["admin","dispatcher"]}><Reports /></ProtectedRoute>} />
          <Route path="/admin/map" element={<ProtectedRoute roles={["admin","dispatcher"]}><MapView /></ProtectedRoute>} />
          <Route path="/admin/address-book" element={<ProtectedRoute roles={["admin","dispatcher"]}><AddressBook /></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute roles={["admin"]}><Settings /></ProtectedRoute>} />

          {/* Customer */}
          <Route path="/portal" element={<ProtectedRoute roles={["customer"]}><Dashboard /></ProtectedRoute>} />
          <Route path="/portal/orders" element={<ProtectedRoute roles={["customer"]}><OrdersList /></ProtectedRoute>} />
          <Route path="/portal/orders/new" element={<ProtectedRoute roles={["customer"]}><OrderCreate /></ProtectedRoute>} />
          <Route path="/portal/orders/:id" element={<ProtectedRoute roles={["customer"]}><OrderDetail /></ProtectedRoute>} />
          <Route path="/portal/address-book" element={<ProtectedRoute roles={["customer"]}><AddressBook /></ProtectedRoute>} />
          <Route path="/portal/templates" element={<ProtectedRoute roles={["customer"]}><Templates /></ProtectedRoute>} />

          {/* Driver */}
          <Route path="/driver" element={<ProtectedRoute roles={["driver"]}><DriverTasks /></ProtectedRoute>} />
          <Route path="/driver/payouts" element={<ProtectedRoute roles={["driver"]}><DriverMyPayouts /></ProtectedRoute>} />
          <Route path="/driver/orders/:id" element={<ProtectedRoute roles={["driver"]}><DriverOrderDetail /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
